import Axios, { AxiosPromise, AxiosResponse } from "axios";
import moment from "moment";
import { oc } from "ts-optchain";

import { DataUtil } from "./DataUtil";
import { DealernetApi } from "./DealernetApi";

import {
    ICreditDetailLineItem,
    ICreditDetailModel,
    ICreditMemoDetail,
    ICreditMemoDto,
    ICreditMemoLineItemDto,
    ICreditMemoRowItem,
    InvoiceSource,
    LineItemType,
    ISearchByDocumentOrPartRequest,
} from "@/types";

/**
 * API for Credits
 * @see https://rapidparts.atlassian.net/wiki/spaces/PA/pages/928382984/Credits
 */
export class CreditsService {
    /**
     * Gets credits ordered by date
     *
     * @static
     * @param rq The request parameters
     */
    public static async getCredits(rq: ICreditsRequest): Promise<ICreditMemoRowItem[]> {
        const response = await DealernetApi.client().post("/Credit/CreditsByDate", rq);

        const data = oc(response as AxiosResponse<ICreditMemoDto[]>).data([]);
        
        if (data.length == 0) {
            return new Array<ICreditMemoRowItem>();
        }
        return data.map((item) => this.mapToRowItem(item));
    }

    /**
     * Gets the credit detail
     *
     * @static
     * @param params The request parameters
     * @example /Credit/CreditById?accountId=925&userId=30266&creditMemoId=6306719&invoiceSource=Rapidparts
     */
    public static async getCreditDetailById(params: IGetCreditDetailRequest): Promise<ICreditDetailModel | undefined> {
        const response = await DealernetApi.client().get("/Credit/CreditById", { params });

        const data = oc(response as AxiosResponse<ICreditMemoDetail>).data();

        if (!data) {
            return undefined;
        }

        return this.mapToCreditDetailModel(params, data);
    }

    public static async getCreditDetailByInvoice(params: any): Promise<any>{
        const response = await DealernetApi.client().get("/Credit/CreditByInvoice", { params });

        const data = oc(response as AxiosResponse<ICreditMemoDetail>).data();

        if (!data) {
            return undefined;
        }

        return this.mapToCreditDetailModel(params, data);
    }

    public static getExportAction() {
        return `${DealernetApi.getBaseURL()}/Credit/PrintCredits`;
    }

    public static searchCreditMemosByDocumentOrPart(
        req: ISearchByDocumentOrPartRequest
    ): AxiosPromise<ICreditMemoDto[]> {
        return DealernetApi.client().post("Invoice/SearchCreditMemosByDocumentOrPart", req);
    }

    private static mapToRowItem(item: ICreditMemoDto): ICreditMemoRowItem {
        return {
            id: item.Id,
            invoiceDate: item.InvoiceDate,
            invoiceNumber: item.InvoiceNumber,
            invoiceSource: item.InvoiceSource,
            referenceNumber: item.ReferenceNumber,
            purchaseOrderNumber: item.PurchaseOrderNumber,
            currencyCode: item.BillingCurrencyIndicator,
            freight: item.FreightChargeAmount,
            total: item.InvoiceTotalAmount,
            exportKey: this.createExportKey(item),
        };
    }

    private static mapToCreditDetailModel(
        request: IGetCreditDetailRequest,
        data: ICreditMemoDetail
    ): ICreditDetailModel {
        const lineItems = oc(data).LineItems([]);

        const isMcfa = data.InvoiceSource === InvoiceSource.Mcfa;

        const invoiceDate = data.InvoiceDate ? moment(data.InvoiceDate).format("l") : "No invoice date";

        return {
            // Header
            invoiceNumber: data.InvoiceNumber,
            invoiceDate,
            referenceNumbers: data.ReferenceNumber,
            purchaseOrderNumber: this.getPurchaseOrderNumbers(lineItems),
            warehouseNumber: data.WarehouseNumber,
            showWarehouse: isMcfa,
            showReferenceNumbers: data.InvoiceSource === InvoiceSource.Rapidparts && lineItems.length > 0,
            billTo: {
                Name: data.AddressName,
                AddressLine1: data.AddressLine1,
                AddressLine2: data.AddressLine2,
                AddressLine3: data.AddressLine3,
            },
            oneBillReferenceNumber: data.ReferenceNumber,
            shipToName: data.ShipToName,

            // Grid
            lineItems: lineItems.map((item, index, array) => this.mapToCreditDetailLineItem(item, index, array)),
            currencyCode: data.BillingCurrencyIndicator,
            subTotal: data.SubTotal,
            subTotalCores: data.SubTotalCores,
            restockFee: data.CashDiscountAmount ? data.CashDiscountAmount : data.InvoiceFreightTotal,
            dealerServiceFee: data.DealerServiceFee,
            dealerClaimAmount: data.DealerClaimAmount,
            total: data.InvoiceTotalAmount,

            // Flags
            isMcfa,
            isFromClaim: this.generatedFromClaim(data),
            isFromReturn: this.generatedFromReturn(data),

            // Export/Print
            accountId: request.accountId,
            userId: request.userId,
            exportKey: this.createExportKey(data),
            exportAction: this.getExportAction(),
            headerNotes: data.HeaderNotes
        };
    }

    private static mapToCreditDetailLineItem(
        item: ICreditMemoLineItemDto,
        index: number,
        list: ICreditMemoLineItemDto[]
    ): ICreditDetailLineItem {
        const rowSpan = DataUtil.computeRowSpan(item, index, list);

        return {
            rowSpan,
            source: item.SalesOrderNumber,
            type: item.PartNumber !== "M" ? LineItemType.Data : LineItemType.Comment,
            line: DataUtil.formatLineNumber(item.RecordSequence),
            partNumber: item.PartNumber,
            description: item.PartDescription,
            poNumber: item.CustomerOrderNumber,
            htcCode: item.HarmonizingCode,
            shipped: item.PickedQuantity,
            price: item.BillingPrice,
            coreCost: item.CoreChargeAmount,
            extPrice: item.ExtendedPrice,
            relatedRoute: rowSpan ? this.getRelatedRoute(item, list) : undefined,
            comments: item.Comments
        };
    }

    private static getRelatedRoute(item: ICreditMemoLineItemDto, list: ICreditMemoLineItemDto[]): any {
        const hasRelatedClaim = list.find((x) => x.SalesOrderNumber === item.SalesOrderNumber && item.RelatedClaim);

        const claimNumber = oc(hasRelatedClaim).RelatedClaim.ClaimNumber();

        if (claimNumber) {
            return {
                name: "ClaimDetails",
                query: {
                    claimNumber,
                },
            };
        }

        const hasRelatedReturn = list.find((x) => x.SalesOrderNumber === item.SalesOrderNumber && item.RelatedReturn);

        const returnId = oc(hasRelatedReturn).RelatedReturn.Id();

        if (returnId && hasRelatedReturn) {
            const relatedReturn = hasRelatedReturn.RelatedReturn;

            return {
                name: "ReturnDetails",
                query: {
                    returnId,
                    salesOrg: relatedReturn.SalesOrganization,
                    accountId: relatedReturn.AccountId,
                    returnSource: relatedReturn.Source,
                },
            };
        }

        return null;
    }

    private static generatedFromClaim(credit: ICreditMemoDto): boolean {
        return credit.LineItems.some((p) => p.RelatedClaim);
    }

    private static generatedFromReturn(credit: ICreditMemoDto): boolean {
        return credit.LineItems.some((p) => p.RelatedReturn);
    }

    private static createExportKey(item: ICreditMemoDto): string {
        return `${item.Id}:${item.InvoiceSource}`;
    }

    private static getPurchaseOrderNumbers(lineItems: ICreditMemoLineItemDto[]): string {
        const distinctOrderNumbers =
            lineItems.length >= 1
                ? lineItems.map((x) => x.CustomerOrderNumber).filter((x, i, a) => a.indexOf(x) === i)
                : [];

        return distinctOrderNumbers.join(", ");
    }
}

export interface ICreditsRequest {
    accountId: number;
    userId: number;
    startDate: Date;
    endDate: Date;
    dealerCodes: string[];
}

export interface IGetCreditDetailRequest {
    accountId: number;
    userId: number;
    creditMemoId: number;
    invoiceSource: InvoiceSource;
}
